import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './SpamhausTool.css';

const SpamhausTool = () => {
  const navigate = useNavigate();
  const [domains, setDomains] = useState('');
  const [submissionType, setSubmissionType] = useState('domain'); // Fixed to domain
  const [threatType, setThreatType] = useState('spam'); // User selectable threat type
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);

  // Threat types based on Spamhaus API
  const threatTypes = [
    { value: 'spam', label: 'Spam' },
    { value: 'adware', label: 'Adware' },
    { value: 'compromised', label: 'Compromised' },
    { value: 'disposable', label: 'Disposable' },
    { value: 'phish', label: 'Phish' },
    { value: 'redirector', label: 'Redirector' },
    { value: 'scam', label: 'Scam' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!domains.trim() || !reason.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    if (reason.length > 255) {
      alert('Reason must be 255 characters or less');
      return;
    }

    setIsSubmitting(true);
    setSubmissionResult(null);

    try {
      const token = localStorage.getItem('token');
      // Use environment variable for API base URL
      const API_BASE_URL = process.env.REACT_APP_API_URL;
      const response = await fetch(`${API_BASE_URL}/spamhaus/submit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          domains: domains,
          threatType: threatType, // Use selected threat type
          reason: reason
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSubmissionResult(data);
        setDomains('');
        setReason('');
      } else {
        const errorData = await response.json();
        let errorMessage = `Submission failed: ${errorData.message}`;
        
        if (errorData.hint) {
          errorMessage += `\n\nTip: ${errorData.hint}`;
        }
        
        if (errorData.originalInput) {
          console.log('Original input that failed:', errorData.originalInput);
        }
        
        alert(errorMessage);
      }
    } catch (error) {
      console.error('Error submitting domains:', error);
      alert('Network error occurred. Please try again.');
    }

    setIsSubmitting(false);
  };

  const getDomainCount = () => {
    if (!domains.trim()) return 0;
    return domains.split(/[\n,\s]+/).filter(domain => domain.trim().length > 0).length;
  };

  const handleNewSubmission = () => {
    setSubmissionResult(null);
    setDomains('');
    setReason('');
  };

  return (
    <div className="spamhaus-tool">
      <div className="tool-header">
        <div className="header-content">
          <button onClick={() => navigate('/tools')} className="back-btn">
            ← Back to Tools
          </button>
          <h1>Spamhaus Domain Reporter</h1>
          <p>Report malicious domains to Spamhaus Project for community protection</p>
        </div>
      </div>

      <div className="tool-container">
        {!submissionResult ? (
          <div className="submission-form">
            <form onSubmit={handleSubmit}>
              <div className="form-section">
                <h3>Domain Submission</h3>
                
                <div className="form-group">
                  <label htmlFor="domains">
                    Domains to Report *
                    <span className="domain-count">({getDomainCount()} domains)</span>
                  </label>
                  <textarea
                    id="domains"
                    value={domains}
                    onChange={(e) => setDomains(e.target.value)}
                    placeholder="Enter domains or URLs (one per line or comma-separated):&#10;example1.com&#10;https://malicious-site.org/page&#10;www.suspicious-domain.net&#10;bad-domain.com"
                    rows="8"
                    required
                  />
                  <small className="form-help">
                    Enter multiple domains or full URLs. The system will automatically extract domain names. You can include:
                    <br />• Full URLs (https://example.com/path)
                    <br />• Domain names (example.com)
                    <br />• Domains with www (www.example.com)
                    <br />Maximum 100 domains per submission.
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="submissionType">Submission Type *</label>
                  <select
                    id="submissionType"
                    value={submissionType}
                    onChange={(e) => setSubmissionType(e.target.value)}
                    required
                  >
                    <option value="domain">Domain</option>
                  </select>
                  <small className="form-help">
                    Report malicious domains to Spamhaus Project database.
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="threatType">Threat Type *</label>
                  <select
                    id="threatType"
                    value={threatType}
                    onChange={(e) => setThreatType(e.target.value)}
                    required
                  >
                    {threatTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                  <small className="form-help">
                    Select the type of threat this domain represents.
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="reason">
                    Reason for Reporting *
                    <span className="char-count">({reason.length}/255)</span>
                  </label>
                  <textarea
                    id="reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Please provide a detailed reason for reporting these domains..."
                    rows="4"
                    maxLength="255"
                    required
                  />
                  <small className="form-help">
                    Provide a clear explanation of why these domains should be reported.
                  </small>
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" disabled={isSubmitting} className="submit-btn">
                  {isSubmitting ? (
                    <>
                      <span className="spinner"></span>
                      Submitting Domains...
                    </>
                  ) : (
                    `Submit ${getDomainCount()} Domain${getDomainCount() !== 1 ? 's' : ''} to Spamhaus`
                  )}
                </button>
              </div>
            </form>
          </div>
        ) : (
          <div className="submission-results">
            <div className="results-header">
              <h3>Submission Complete</h3>
              <p>Submission ID: <strong>{submissionResult.submissionId}</strong></p>
            </div>

            <div className="results-summary">
              <div className="summary-cards">
                <div className="summary-card success">
                  <div className="card-number">{submissionResult.summary.successful}</div>
                  <div className="card-label">Successfully Submitted</div>
                </div>
                <div className="summary-card warning">
                  <div className="card-number">{submissionResult.summary.alreadyReported}</div>
                  <div className="card-label">Already Reported</div>
                </div>
                <div className="summary-card error">
                  <div className="card-number">{submissionResult.summary.failed}</div>
                  <div className="card-label">Failed</div>
                </div>
              </div>
            </div>

            <div className="results-details">
              <h4>Detailed Results</h4>
              <div className="results-table">
                <div className="table-header">
                  <div>Domain</div>
                  <div>Status</div>
                  <div>Details</div>
                </div>
                {submissionResult.results.map((result, index) => (
                  <div key={index} className={`table-row ${result.success ? (result.alreadyReported ? 'warning' : 'success') : 'error'}`}>
                    <div className="domain-cell">{result.domain}</div>
                    <div className="status-cell">
                      {result.success ? (
                        result.alreadyReported ? (
                          <span className="status-badge warning">Already Reported</span>
                        ) : (
                          <span className="status-badge success">Submitted</span>
                        )
                      ) : (
                        <span className="status-badge error">Failed</span>
                      )}
                    </div>
                    <div className="details-cell">
                      {result.success && !result.alreadyReported ? (
                        <code>{result.data.id}</code>
                      ) : result.alreadyReported ? (
                        'Domain already in Spamhaus database'
                      ) : (
                        result.error
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="results-actions">
              <button onClick={handleNewSubmission} className="primary-btn">
                Submit More Domains
              </button>
              <button onClick={() => navigate('/spamhaus/history')} className="secondary-btn">
                View Submission History
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SpamhausTool;
